import React, { useEffect, useState } from 'react';

import { isRecord, textOf } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';

type Done = () => void;

const ACTION_LABELS: Record<string, string> = {
  OPEN_PRODUCT: '打开线路详情',
  OPEN_CUSTOMER_SERVICE: '打开在线客服',
  OPEN_ENTERPRISE_WECHAT_GUIDE: '添加服务向导',
  SWITCH_CITY: '切换城市',
  OPEN_AGREEMENT: '查看预订须知',
};

function actionLabel(actionId: string) {
  return ACTION_LABELS[actionId] ?? actionId;
}

function actionIdFromInput(value: string) {
  const trimmed = value.trim();
  return Object.entries(ACTION_LABELS).find(([, label]) => label === trimmed)?.[0] ?? trimmed;
}

function parseActionInput(value: string) {
  return value.split(',').map(actionIdFromInput).filter(Boolean);
}

function formatActionInput(value: unknown) {
  return Array.isArray(value) ? value.map((item) => actionLabel(String(item))).join(', ') : '';
}

function parseJsonRecord(value: string): JsonRecord {
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export const typedCommandEndpoints = {
  cityCreate: (appId: string) => `/api/admin/m2/apps/${appId}/cities`,
  runtime: (appId: string) => `/api/admin/m2/apps/${appId}/runtime`,
  skuCreate: (productId: string) => `/api/admin/m2/products/${productId}/skus`,
  cmsPageCreate: (appId: string) => `/api/admin/m2/apps/${appId}/cms/pages`,
  cmsComponentCreate: (appId: string, pageId: string) =>
    `/api/admin/m2/apps/${appId}/cms/pages/${pageId}/components`,
};

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>();
  async function run(command: () => Promise<unknown>, done: Done) {
    setBusy(true);
    setResult(undefined);
    try {
      await command();
      setResult('命令执行成功');
      done();
    } catch {
      setResult('操作未完成，请检查填写内容或稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return { busy, result, run };
}

function Result({ value }: { value: string | undefined }) {
  return value ? <span className="command-result">{value}</span> : null;
}

export function CityCreateForm({
  api,
  appId,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  onDone: Done;
}) {
  const submit = useSubmit();
  const [regionId, setRegionId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  return (
    <form
      className="typed-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(typedCommandEndpoints.cityCreate(appId), 'POST', {
              regionId,
              code,
              name,
              sortOrder: Number(sortOrder),
            }),
          onDone,
        );
      }}
    >
      <h3>创建城市</h3>
      <label>
        地区 ID
        <input required value={regionId} onChange={(event) => setRegionId(event.target.value)} />
      </label>
      <label>
        城市编码
        <input required value={code} onChange={(event) => setCode(event.target.value)} />
      </label>
      <label>
        城市名称
        <input required value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        排序
        <input
          type="number"
          required
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
        />
      </label>
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          {submit.busy ? '提交中…' : '创建城市'}
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function RuntimeConfigForm({
  api,
  appId,
  initial,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  initial: JsonRecord;
  onDone: Done;
}) {
  const submit = useSubmit();
  const [defaultCityId, setDefaultCityId] = useState('');
  const [defaultOrderMode, setDefaultOrderMode] = useState('STANDARD');
  const [themeConfig, setThemeConfig] = useState('{}');
  const [customerServiceConfig, setCustomerServiceConfig] = useState('{}');
  const [runtimeFlags, setRuntimeFlags] = useState('{}');
  useEffect(() => {
    setDefaultCityId(
      textOf(initial, 'defaultCityId') === '—' ? '' : textOf(initial, 'defaultCityId'),
    );
    setDefaultOrderMode(
      textOf(initial, 'defaultOrderMode') === '—'
        ? 'STANDARD'
        : textOf(initial, 'defaultOrderMode'),
    );
    setThemeConfig(
      JSON.stringify(isRecord(initial.themeConfig) ? initial.themeConfig : {}, null, 2),
    );
    setCustomerServiceConfig(
      JSON.stringify(
        isRecord(initial.customerServiceConfig) ? initial.customerServiceConfig : {},
        null,
        2,
      ),
    );
    setRuntimeFlags(
      JSON.stringify(isRecord(initial.runtimeFlags) ? initial.runtimeFlags : {}, null, 2),
    );
  }, [initial]);
  return (
    <form
      className="typed-form wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(typedCommandEndpoints.runtime(appId), 'PUT', {
              ...(defaultCityId ? { defaultCityId } : {}),
              defaultOrderMode,
              themeConfig: JSON.parse(themeConfig) as unknown,
              customerServiceConfig: JSON.parse(customerServiceConfig) as unknown,
              runtimeFlags: JSON.parse(runtimeFlags) as unknown,
            }),
          onDone,
        );
      }}
    >
      <h3>运行配置</h3>
      <label>
        默认城市 ID
        <input value={defaultCityId} onChange={(event) => setDefaultCityId(event.target.value)} />
      </label>
      <label>
        默认下单模式
        <select
          value={defaultOrderMode}
          onChange={(event) => setDefaultOrderMode(event.target.value)}
        >
          <option value="STANDARD">标准预订（先填资料后支付）</option>
          <option value="QUICK">快捷下单（先支付后补资料）</option>
        </select>
      </label>
      <label>
        主题配置 JSON
        <textarea value={themeConfig} onChange={(event) => setThemeConfig(event.target.value)} />
      </label>
      <label>
        客服配置 JSON
        <textarea
          value={customerServiceConfig}
          onChange={(event) => setCustomerServiceConfig(event.target.value)}
        />
      </label>
      <label>
        运行开关 JSON
        <textarea value={runtimeFlags} onChange={(event) => setRuntimeFlags(event.target.value)} />
      </label>
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          保存运行配置
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function SkuCreateForm({
  api,
  productId,
  onDone,
}: {
  api: AdminApiClient;
  productId: string;
  onDone: Done;
}) {
  const submit = useSubmit();
  const [form, setForm] = useState({
    skuCode: '',
    skuName: '',
    serviceMode: 'CHARTER_GUIDE_PACKAGE',
    pricingMode: 'PER_CAR',
    salePriceMinorUnits: '',
    guideMode: 'INCLUDED',
    ticketMode: 'INCLUDED',
    dateRuleId: '',
    refundPolicyId: '',
    includedItems: '',
    excludedItems: '',
  });
  const set = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="typed-form wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(typedCommandEndpoints.skuCreate(productId), 'POST', {
              ...form,
              salePriceMinorUnits: form.salePriceMinorUnits,
              includedItems: form.includedItems
                .split('\n')
                .map((v) => v.trim())
                .filter(Boolean),
              excludedItems: form.excludedItems
                .split('\n')
                .map((v) => v.trim())
                .filter(Boolean),
            }),
          onDone,
        );
      }}
    >
      <h3>创建 SKU 套餐</h3>
      <label>
        SKU 编码
        <input required value={form.skuCode} onChange={(e) => set('skuCode', e.target.value)} />
      </label>
      <label>
        SKU 名称
        <input required value={form.skuName} onChange={(e) => set('skuName', e.target.value)} />
      </label>
      <label>
        服务模式
        <select value={form.serviceMode} onChange={(e) => set('serviceMode', e.target.value)}>
          <option value="CHARTER_GUIDE_PACKAGE">包车 + 向导 + 套餐</option>
          <option value="TRANSFER_TICKET_PACKAGE">接送 + 票务 + 套餐</option>
          <option value="CARPOOL_GUIDE_PACKAGE">拼车 + 向导 + 套餐</option>
        </select>
      </label>
      <label>
        计价方式
        <select value={form.pricingMode} onChange={(e) => set('pricingMode', e.target.value)}>
          <option value="PER_PERSON">按人</option>
          <option value="PER_CAR">按车</option>
          <option value="PER_PACKAGE">按套餐</option>
        </select>
      </label>
      <label>
        售价（整数分字符串）
        <input
          required
          inputMode="numeric"
          pattern="[0-9]+"
          value={form.salePriceMinorUnits}
          onChange={(e) => set('salePriceMinorUnits', e.target.value)}
        />
      </label>
      <label>
        向导方式
        <select value={form.guideMode} onChange={(e) => set('guideMode', e.target.value)}>
          <option value="INCLUDED">包含</option>
          <option value="NOT_INCLUDED">不包含</option>
        </select>
      </label>
      <label>
        票务方式
        <select value={form.ticketMode} onChange={(e) => set('ticketMode', e.target.value)}>
          <option value="INCLUDED">包含</option>
          <option value="SELF_PAY">自理</option>
          <option value="NOT_INCLUDED">不包含</option>
        </select>
      </label>
      <label>
        服务日期规则 ID
        <input
          required
          value={form.dateRuleId}
          onChange={(e) => set('dateRuleId', e.target.value)}
        />
      </label>
      <label>
        退款规则 ID
        <input
          required
          value={form.refundPolicyId}
          onChange={(e) => set('refundPolicyId', e.target.value)}
        />
      </label>
      <label>
        包含服务项（每行一项）
        <textarea
          value={form.includedItems}
          onChange={(e) => set('includedItems', e.target.value)}
        />
      </label>
      <label>
        不含服务项（每行一项）
        <textarea
          value={form.excludedItems}
          onChange={(e) => set('excludedItems', e.target.value)}
        />
      </label>
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          创建 SKU
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function CmsPageCreateForm({
  api,
  appId,
  templates,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  templates: JsonRecord[];
  onDone: Done;
}) {
  const submit = useSubmit();
  const [templateId, setTemplateId] = useState('');
  const [pageCode, setPageCode] = useState('');
  const [scopeType, setScopeType] = useState('APP');
  const [scopeId, setScopeId] = useState('');
  return (
    <form
      className="typed-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(typedCommandEndpoints.cmsPageCreate(appId), 'POST', {
              templateId,
              pageCode,
              scopeType,
              ...(scopeType === 'CITY'
                ? { cityId: scopeId }
                : scopeType === 'PRODUCT'
                  ? { productSpuId: scopeId }
                  : {}),
            }),
          onDone,
        );
      }}
    >
      <h3>创建页面实例</h3>
      <label>
        页面模板
        <select required value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">请选择</option>
          {templates.map((item) => (
            <option key={textOf(item, 'id')} value={textOf(item, 'id')}>
              {textOf(item, 'name')} · {textOf(item, 'templateCode')}
            </option>
          ))}
        </select>
      </label>
      <label>
        页面编码
        <input required value={pageCode} onChange={(e) => setPageCode(e.target.value)} />
      </label>
      <label>
        覆盖范围
        <select value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
          <option value="APP">APP</option>
          <option value="CITY">CITY</option>
          <option value="PRODUCT">PRODUCT</option>
        </select>
      </label>
      {scopeType !== 'APP' && (
        <label>
          {scopeType} ID
          <input required value={scopeId} onChange={(e) => setScopeId(e.target.value)} />
        </label>
      )}
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          创建页面
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function CmsComponentForm({
  api,
  appId,
  pageId,
  definitions,
  expectedDraftVersion,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  definitions: JsonRecord[];
  expectedDraftVersion: number;
  onDone: Done;
}) {
  const submit = useSubmit();
  const [definitionId, setDefinitionId] = useState('');
  const [instanceKey, setInstanceKey] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [props, setProps] = useState('{}');
  const [bindings, setBindings] = useState('{}');
  const [actionIds, setActionIds] = useState('');
  const selected = definitions.find((item) => textOf(item, 'id') === definitionId);
  const allowed =
    selected && Array.isArray(selected.allowedActionIds)
      ? selected.allowedActionIds.map(String)
      : [];
  return (
    <form
      className="typed-form wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(typedCommandEndpoints.cmsComponentCreate(appId, pageId), 'POST', {
              componentDefinitionId: definitionId,
              instanceKey,
              sortOrder: Number(sortOrder),
              props: JSON.parse(props) as unknown,
              dataBindings: JSON.parse(bindings) as unknown,
              actionIds: parseActionInput(actionIds),
              expectedDraftVersion,
            }),
          onDone,
        );
      }}
    >
      <h3>添加白名单组件</h3>
      <label>
        组件定义
        <select required value={definitionId} onChange={(e) => setDefinitionId(e.target.value)}>
          <option value="">请选择</option>
          {definitions.map((item) => (
            <option key={textOf(item, 'id')} value={textOf(item, 'id')}>
              {textOf(item, 'displayName')}
            </option>
          ))}
        </select>
      </label>
      <label>
        实例键
        <input required value={instanceKey} onChange={(e) => setInstanceKey(e.target.value)} />
      </label>
      <label>
        排序
        <input
          type="number"
          required
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </label>
      <label>
        展示配置（结构化）
        <textarea value={props} onChange={(e) => setProps(e.target.value)} />
      </label>
      <label>
        数据绑定配置（结构化）
        <textarea value={bindings} onChange={(e) => setBindings(e.target.value)} />
      </label>
      <label>
        受控动作（逗号分隔）
        <input value={actionIds} onChange={(e) => setActionIds(e.target.value)} />
        <small>
          允许值：{allowed.length ? allowed.map(actionLabel).join('、') : '该组件未声明动作'}
        </small>
      </label>
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          添加组件
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function CmsComponentUpdateForm({
  api,
  appId,
  pageId,
  component,
  expectedDraftVersion,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  component: JsonRecord;
  expectedDraftVersion: number;
  onDone: Done;
}) {
  const submit = useSubmit();
  const componentId = textOf(component, 'id');
  const [props, setProps] = useState('{}');
  const [bindings, setBindings] = useState('{}');
  const [actionIds, setActionIds] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  useEffect(() => {
    setProps(JSON.stringify(component.props ?? {}, null, 2));
    setBindings(JSON.stringify(component.dataBindings ?? {}, null, 2));
    setActionIds(formatActionInput(component.actionIds));
    setStatus(textOf(component, 'status') === 'DISABLED' ? 'DISABLED' : 'ACTIVE');
  }, [component]);
  const componentKey = textOf(component, 'componentKey');
  const instanceKey = textOf(component, 'instanceKey');
  const structuredProps = parseJsonRecord(props);
  function setStructuredProp(key: string, value: unknown) {
    setProps(JSON.stringify({ ...structuredProps, [key]: value }, null, 2));
  }
  const heroImages = Array.isArray(structuredProps.images)
    ? structuredProps.images
        .map((entry) => (isRecord(entry) ? textOf(entry, 'url') : String(entry)))
        .filter((entry) => entry !== '—')
    : [];
  const serviceColumns = Array.isArray(structuredProps.columns)
    ? structuredProps.columns.filter(isRecord)
    : [];
  const serviceDefaults = [
    ['ticket', '优惠票务'],
    ['car', '正规车辆'],
    ['guide', '自由出行'],
    ['headset', '优质服务'],
    ['price', '透明报价'],
  ] as const;
  const endpoint = `/api/admin/m2/apps/${appId}/cms/pages/${pageId}/components/${componentId}`;
  return (
    <form
      className="typed-form wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit.run(
          () =>
            api.send(endpoint, 'PATCH', {
              props: JSON.parse(props) as unknown,
              dataBindings: JSON.parse(bindings) as unknown,
              actionIds: parseActionInput(actionIds),
              status,
              expectedDraftVersion,
            }),
          onDone,
        );
      }}
    >
      <h3>编辑组件 · {textOf(component, 'displayName') || textOf(component, 'instanceKey')}</h3>
      {componentKey === 'hero-banner' && (
        <fieldset className="cms-friendly-fields">
          <legend>城市主视觉</legend>
          <label>
            页面标题
            <input
              value={
                textOf(structuredProps, 'title') === '—' ? '' : textOf(structuredProps, 'title')
              }
              onChange={(event) => setStructuredProp('title', event.target.value)}
            />
          </label>
          <label>
            标题下说明
            <input
              value={textOf(structuredProps, 'body') === '—' ? '' : textOf(structuredProps, 'body')}
              onChange={(event) => setStructuredProp('body', event.target.value)}
            />
          </label>
          <label>
            城市轮播图（每行一个 HTTPS 图片地址）
            <textarea
              value={heroImages.join('\n')}
              onChange={(event) =>
                setStructuredProp(
                  'images',
                  event.target.value
                    .split(/\r?\n/u)
                    .map((url) => url.trim())
                    .filter(Boolean)
                    .map((url) => ({
                      url,
                      altText: `${textOf(structuredProps, 'title')}城市风光`,
                    })),
                )
              }
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={structuredProps.autoplay !== false}
              onChange={(event) => setStructuredProp('autoplay', event.target.checked)}
            />
            自动播放轮播图
          </label>
          <label>
            自动切换间隔（毫秒，2000–10000）
            <input
              type="number"
              min="2000"
              max="10000"
              step="500"
              value={Number(structuredProps.interval ?? 4000)}
              onChange={(event) => setStructuredProp('interval', Number(event.target.value))}
            />
          </label>
        </fieldset>
      )}
      {componentKey === 'feature-grid' && (
        <fieldset className="cms-friendly-fields">
          <legend>五项服务保障</legend>
          {serviceDefaults.map(([iconKey, fallback], index) => (
            <label key={iconKey}>
              {index + 1}. {fallback}
              <input
                value={
                  textOf(serviceColumns[index] ?? {}, 'label', 'name') === '—'
                    ? fallback
                    : textOf(serviceColumns[index] ?? {}, 'label', 'name')
                }
                onChange={(event) => {
                  const next = serviceDefaults.map(([nextIcon, nextFallback], nextIndex) => ({
                    iconKey: nextIcon,
                    label:
                      nextIndex === index
                        ? event.target.value
                        : textOf(serviceColumns[nextIndex] ?? {}, 'label', 'name') === '—'
                          ? nextFallback
                          : textOf(serviceColumns[nextIndex] ?? {}, 'label', 'name'),
                  }));
                  setStructuredProp('columns', next);
                }}
              />
            </label>
          ))}
        </fieldset>
      )}
      {componentKey === 'notice' && instanceKey === 'cost-note' && (
        <fieldset className="cms-friendly-fields">
          <legend>费用说明</legend>
          <label>
            分区标题
            <input
              value={
                textOf(structuredProps, 'title') === '—' ? '' : textOf(structuredProps, 'title')
              }
              onChange={(event) => setStructuredProp('title', event.target.value)}
            />
          </label>
          <label>
            说明文字（每行一项）
            <textarea
              value={textOf(structuredProps, 'body') === '—' ? '' : textOf(structuredProps, 'body')}
              onChange={(event) => setStructuredProp('body', event.target.value)}
            />
          </label>
        </fieldset>
      )}
      <details className="advanced-command">
        <summary>高级结构化配置</summary>
        <label>
          展示配置（JSON）
          <textarea value={props} onChange={(event) => setProps(event.target.value)} />
        </label>
      </details>
      <label>
        数据绑定配置（结构化）
        <textarea value={bindings} onChange={(event) => setBindings(event.target.value)} />
      </label>
      <label>
        受控动作（逗号分隔）
        <input value={actionIds} onChange={(event) => setActionIds(event.target.value)} />
      </label>
      <label>
        状态
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="ACTIVE">启用</option>
          <option value="DISABLED">停用</option>
        </select>
      </label>
      <div className="button-row">
        <button className="primary" disabled={submit.busy}>
          保存组件
        </button>
        <button
          type="button"
          className="danger"
          disabled={submit.busy}
          onClick={() => {
            if (!window.confirm('确认删除这个草稿组件？已发布版本不会被直接改写。')) return;
            void submit.run(() => api.send(endpoint, 'DELETE', { expectedDraftVersion }), onDone);
          }}
        >
          删除组件
        </button>
        <Result value={submit.result} />
      </div>
    </form>
  );
}

export function CmsComponentOrderForm({
  api,
  appId,
  pageId,
  components,
  expectedDraftVersion,
  onDone,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  components: JsonRecord[];
  expectedDraftVersion: number;
  onDone: Done;
}) {
  const submit = useSubmit();
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= components.length) return;
    const ids = components.map((component) => textOf(component, 'id'));
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    void submit.run(
      () =>
        api.send(`/api/admin/m2/apps/${appId}/cms/pages/${pageId}/components/reorder`, 'PUT', {
          componentIds: ids,
          expectedDraftVersion,
        }),
      onDone,
    );
  }
  return (
    <div className="typed-form wide">
      <h3>调整草稿图层顺序</h3>
      <div className="cms-order-list">
        {components.map((component, index) => (
          <div key={textOf(component, 'id')}>
            <span>
              {index + 1}. {textOf(component, 'displayName') || textOf(component, 'instanceKey')}
            </span>
            <span className="button-row">
              <button
                type="button"
                disabled={submit.busy || index === 0}
                onClick={() => move(index, -1)}
              >
                上移
              </button>
              <button
                type="button"
                disabled={submit.busy || index === components.length - 1}
                onClick={() => move(index, 1)}
              >
                下移
              </button>
            </span>
          </div>
        ))}
      </div>
      <Result value={submit.result} />
    </div>
  );
}
